<template>
  <div>
    <!-- The method of the component instance will be called through ref(pbxCardRef) to facilitate the reuse of code logic -->
    <opc-card-cascade
      v-if="!boughtPbxBasePlan"
      ref="pbxCardRef"
      v-model="billingCycleModel"
      divide-line
      :tracking-data='planAndCycleTrackingData'
      :config="cycleAndPlansConfig"
      :billing-cycle-switch-visible="billingCycleSwitchVisible"
      @change="switchPlansCycle">
      <template #group1-title>
        <div>
          <span id="group1-title">{{ callingFeeLabel }}</span>
          <zm-tooltip v-model='callingfeesToolTip' placement="top" disallow-learn-about>
            <template #content>
              <p v-html="$t('billing.pbx.calling_fee_tips', [activeRegionLabel])"></p>
            </template>
            <tips-icon data-testid="checkout-button-221" :aria-label="'Learn More about ' + callingFeeLabel" />
          </zm-tooltip>
        </div>
      </template>
    </opc-card-cascade>
    <ZP0ABJ v-if='isVisible0ABJValidate' />
    <appeal-byoc v-else />
    <appeal-cycle v-if="boughtPbxBasePlan" :cycle="i18n_cycle(planBillInfo.billing_cycle)" :payment="planBillInfo.payment" />
  </div>
</template>
<script>
import ZP0ABJ from '../component/0ABJ.vue';
import AppealByoc from '../component/appeal-byoc.vue';
import AppealCycle from '../component/appeal-cycle.vue';
import { i18n_cycle } from '@/utils/i18n-shortcuts';
import OpcCardCascade from '../component/opc-card-cascade';
import { mapGetters, mapMutations, mapState } from 'vuex';
import { BILLING_CYCLE } from '@/common/constants';
import { CURRENCY_COOKIE_KEY } from "@/utils/currency";
import { groupBy, get } from 'lodash';
import { computedPbxBasePlans, computedPbxCallingPlans, computedPbxPlans, PBX_SUB_TYPES } from '@/common/service/computers/plans/pbx';
import mxSoftBundleCustom from '../mixin/soft-bundle-pbx';
import { regions, PBX_GLOBAL } from '../helper/constants';
import { getZPCallingFeesText } from '@/plans/ZOOM_PHONE/helper/utils';

export default {
  mixins: [mxSoftBundleCustom],
  props: {
    activeRegionKey: {
      type: String,
      required: true,
    },
    planBillInfo: {
      type: Object,
      required: true,
    }
  },
  components: {
    ZP0ABJ,
    AppealByoc,
    AppealCycle,
    OpcCardCascade,
  },
  data() {
    return {
      callingfeesToolTip: false,
    };
  },
  computed: {
    ...mapState({
      japanIP: (state) => state.Common.japanIP,
    }),
    ...mapGetters(['boughtPlans', 'pbxBaseInfo', 'getSelectedPlans', 'planMapFlat']),
    billingCycleModel: {
      get() {
        return [this.pbxBaseInfo.billingCycle, this.pbxBaseInfo.basePlanCode];
      },
      set(val) {
        const [billingCycle, usageFeePlanCode] = val
        this.setPbxBillingCycle(billingCycle);
        this.setPbxBasePlanCode(usageFeePlanCode);
      }
    },
    isVisible0ABJValidate() {
      if (this.$context.is.add2cart && this.activeRegionKey === PBX_GLOBAL.GS) {
        // japan currency or japan location
        if (!this.$config('enable0ABJTool')) {
          return false;
        }
        const currency = this.$cookie.get(CURRENCY_COOKIE_KEY);
        if (currency === "JPY" || this.japanIP) {
          return true;
        }
        return false;
      }
      return false;
    },
    selectedPbxPlans() {
      return computedPbxPlans(this.getSelectedPlans);
    },
    selectedCallingPlan() {
      return computedPbxCallingPlans(this.selectedPbxPlans)[0];
    },
    allPbxPlans() {
      return computedPbxPlans(this.planMapFlat);
    },
    boughtPbxBasePlan() {
      const plans = computedPbxBasePlans(this.boughtPlans);
      return plans?.[0];
    },
    basePlans() {
      return computedPbxBasePlans(this.planMapFlat);
    },
    selectedBasePlan() {
      return computedPbxBasePlans(this.selectedPbxPlans)?.[0];
    },
    activeRegionLabel() {
      return this.getRegionLabelById(this.activeRegionKey)
    },

    callingFeeLabel() {
      if(!this.selectedCallingPlan) return this.$t('billing.pbx.calling_fee_label.unlimited');
      if(this.selectedCallingPlan.sub_type === PBX_SUB_TYPES.UNLIMITED) return this.$t('billing.pbx.calling_fee_label.unlimited');
      if(this.selectedCallingPlan.sub_type === PBX_SUB_TYPES.METERED) return this.$t('billing.pbx.calling_fee_label.metered');
      return this.$t('billing.pbx.calling_fee_label.unlimited');
    },
    planAndCycleTrackingData() {
      return [{ buyInteraction: 'zoom phone subscription cycle', }, { buyInteraction: 'zoom phone international calling fees', },];
    },
    costMap() {
      const selectedCategoryNumberMap = this.selectedPbxPlans
      .filter(item => (
        item.sub_type === PBX_SUB_TYPES.UNLIMITED ||
        item.sub_type === PBX_SUB_TYPES.METERED ||
        item.sub_type === PBX_SUB_TYPES.TOLLFREE ||
        item.sub_type === PBX_SUB_TYPES.PHONE_NUMBER
      ))
      .reduce((pre, item) => {
        pre[item.category] = item.cartInfo.selected_number;
        return pre;
      }, {});
      const monthlyPlans = this.allPbxPlans.filter(item => selectedCategoryNumberMap[item.category] && item.billing_cycle === BILLING_CYCLE.MONTHLY);
      const annualPlans = this.allPbxPlans.filter(item => selectedCategoryNumberMap[item.category] && item.billing_cycle === BILLING_CYCLE.ANNUAL);
      const _costMap = {
        [BILLING_CYCLE.MONTHLY]: monthlyPlans.reduce((perviousValue, currentValue) => {
          return perviousValue + currentValue.amount * selectedCategoryNumberMap[currentValue.category];
        }, 0),
        [BILLING_CYCLE.ANNUAL]: annualPlans.reduce((perviousValue, currentValue) => {
          return perviousValue + currentValue.amount * selectedCategoryNumberMap[currentValue.category];
        }, 0),
      };
      return _costMap;
    },
    billingCycleSwitchVisible() {
      if (this.soft_bundle_hiddenCycleSelection) return false;
      return true;
    },
    cycleAndPlansConfig() {
      const { annual, monthly, payAsYouGo } = groupBy(this.basePlans, 'billing_cycle');
      return {
        group0: {
          title: this.$t('billing.select_billing_cycle'),
        },
        group1: {
          slot: 'group1-title',
        },
        options: ['monthly', 'annual'].map(cycle => {
          return {
              value: cycle,
              title: this.$t(`billing.billing_cycle.${cycle}`),
              subTitle: this.$money(this.costMap[cycle], { forceDiff: true }),
              description: cycle === 'monthly' ? this.$t('billing.monthly_cost') : this.$t('billing.annual_cost'),
              showSave: BILLING_CYCLE.ANNUAL === cycle,
              ...this.costMap,
              options: [...payAsYouGo, ...(cycle === 'monthly' ? monthly : annual)].map(plan => {
                return {
                  value: plan.plan_code,
                  title: this.getCallingFeesText(plan).title,
                  subTitle: this.$money(plan.amount, { forceDiff: true }),
                  description: this.getCallingFeesText(plan).unit,
                }
              })
            }
        })
      }
    },
  },
  methods: {
    ...mapMutations({
      setPbxBillingCycle: 'setPbxBillingCycle',
      setPbxBasePlanCode: 'setPbxBasePlanCode',
      updateSelectPlan: 'updateSelectPlan',
    }),
    getCallingFeesText: getZPCallingFeesText,
    i18n_cycle,
    handleBillingCycleChange(value) {
      const selectedCategoryNumberMap = this.selectedPbxPlans
      .filter(item => item.sub_type !== PBX_SUB_TYPES.BASE)
      .reduce((pre, item) => {
        pre[item.category] = item.cartInfo.selected_number;
        return pre;
      }, {});
      this.switchPlansBillingCycle(selectedCategoryNumberMap, value);
    },
    switchPlansBillingCycle(categoryNumberMap, billingCycle) {
      this.allPbxPlans
        // get selected plans
        .filter(item => categoryNumberMap[item.category])
        .forEach(item => {
          if (item.billing_cycle === billingCycle) {
            // add plan to cart
            this.updateSelectPlan({ plan_code: item.plan_code, cartInfo: { selected_number: categoryNumberMap[item.category] } })
          } else {
            // remove selected plans
            this.updateSelectPlan({ plan_code: item.plan_code, cartInfo: { selected_number: 0 } })
          }
        })

      this.$emit('cycle-change')
    },
    handleBasePlanChange(value) {
      this.updateSelectPlan({
        plan_code: this.selectedBasePlan.plan_code,
        cartInfo: { selected_number: 0 },
      });
      this.updateSelectPlan({
        plan_code: value,
        cartInfo: { selected_number: 1 },
      });
    },
    switchPlansCycle(billingCycleInfo) {
      this.handleBillingCycleChange(billingCycleInfo[0])
      this.handleBasePlanChange(billingCycleInfo[1])
    },
    getRegionLabelById(key) {
      let val = get(regions?.find(r => {
        return r.key === key;
      }), 'label', '');
      if( key === 'GS') return this.$t('billing.pbx.global_select')
      if (val) {
        return this.$t(val);
      }
      return '';
    },
  }
}
</script>
